import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { HiOutlineUserGroup } from "react-icons/hi";
import BasicCustomTable from "../../../component/basicTable/index";
import TextFieldCustom from "../../../component/elements/textfieldCustom";
import axiosInstance from "../../../configuration/request";
import { useSnackbar } from "../../../component/SnackbarContext";
import useDebounce from "../../../utils/useDebounce";
import { adminCustomerColumns } from "../../../constants/adminCustomerTable";
import { getUserManagementPermissions } from "../../../utils/rolesAndPermissions";

function CustomerManagement() {
  const navigate = useNavigate();
  const showSnackbar = useSnackbar();

  const [customers, setCustomers] = useState([]);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");

  const debouncedSearch = useDebounce(searchTerm, 500);

  const permissions = useSelector((state) => state.counter.permissions);
  const rolePermissions = permissions?.[0]?.permissions || [];
  const { canView } = getUserManagementPermissions(rolePermissions);

  const columns = useMemo(() => {
    if (!canView) {
      return adminCustomerColumns.filter((col) => col.field !== "action");
    }
    return adminCustomerColumns;
  }, [canView]);

  const fetchCustomers = useCallback(
    (pageNumber = 1, limit = 10, search = "") => {
      const searchParam = search ? `&search=${encodeURIComponent(search)}` : "";
      axiosInstance
        .get(
          `customer/users?usertype=${encodeURIComponent("normal user")}&page=${pageNumber}&limit=${limit}${searchParam}`
        )
        .then((res) => {
          const formatted = (res?.data?.data || []).map((item) => ({
            id: item?.id || "",
            name: item?.name || "",
            email: item?.email || "",
            phone: item?.phone_number || "",
            gender: item?.gender || "-",
            birth_date: item?.dob || "",
            order_status: item?.user_status ? "Active" : "Inactive",
          }));
          setCustomers(formatted);
          setPage(Number(res?.data?.page) || 1);
          setTotalCount(res?.data?.total || 0);
        })
        .catch((error) => {
          showSnackbar(
            error?.response?.data?.message || "Failed to fetch customers",
            "error"
          );
        });
    },
    [showSnackbar]
  );

  useEffect(() => {
    fetchCustomers(page, rowsPerPage, debouncedSearch);
  }, [debouncedSearch, page, rowsPerPage, fetchCustomers]);

  const handlePageChangeRequest = (newPage, newRowsPerPage) => {
    setPage(newPage);
    setRowsPerPage(newRowsPerPage);
  };

  const handleSearchFilter = (e) => {
    setSearchTerm(e.target.value);
    setPage(1);
  };

  const handleView = (row) => {
    navigate(`/app/customers/view/${row.id}`);
  };

  const handleStatusChange = (status, rowData) => {
    const isActive = status === "active";
    axiosInstance
      .patch(`/customer/users/${rowData?.id}`, { user_status: isActive })
      .then(() => {
        fetchCustomers(page, rowsPerPage, debouncedSearch);
        showSnackbar("Status updated successfully", "success");
      })
      .catch((error) => {
        showSnackbar(
          error?.response?.data?.message || "Failed to update status",
          "error"
        );
      });
  };

  return (
    <div className="px-4 md:px-6">
      <div className="flex flex-col gap-4 py-4">


        <div className="flex w-full max-w-[350px]">
          <TextFieldCustom
            showSearchIcon
            placeholder="Search by email, phone, or name"
            customClass="color-search-textfield"
            fullWidth
            value={searchTerm}
            onChange={handleSearchFilter}
          />
        </div>

        <BasicCustomTable
          columns={columns}
          rows={customers}
          onView={handleView}
          colorHeader={true}
          customClassHeader="table-header-cell-colored"
          tableName="customer management"
          maxHeightClass="max-h-[calc(100vh-280px)]"
          backendPagination={true}
          totalCount={totalCount}
          onPageChangeRequest={handlePageChangeRequest}
          pageLimit={rowsPerPage}
          page={page}
          canView={canView}
          onTagChange={handleStatusChange}
          emptyState="No customers found"
        />
      </div>
    </div>
  );
}

export default CustomerManagement;
